/**
 * LayoutComposer - Drag-and-drop map layout editor
 *
 * Provides an interactive canvas for composing map layouts with:
 * - Draggable and resizable elements (map frame, title, legend, scale bar, north arrow, text boxes)
 * - Element positioning via mouse drag
 * - Element resizing via corner handles
 * - Layout template save/load
 * - Support for multiple map frames
 * - Export to PDF or image
 *
 * **Validates: Requirements 38.1, 38.2, 38.3, 38.4, 38.5, 38.6**
 */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Toolbar,
  Tooltip,
  TextField,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
} from '@mui/material';
import {
  Title as TitleIcon,
  Map as MapIcon,
  FormatListBulleted as LegendIcon,
  Straighten as ScaleBarIcon,
  Navigation as NorthArrowIcon,
  TextFields as TextIcon,
  Save as SaveIcon,
  FolderOpen as LoadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  closeLayoutComposer,
  setCurrentLayout,
  updateLayoutElement,
  addLayoutElement,
  removeLayoutElement,
  fetchLayoutTemplates,
  saveLayoutTemplate,
  deleteLayoutTemplate,
} from '../store/slices/printExportSlice';
import type { LayoutElement, LayoutElementType, MapLayout } from '../types/print';

// ---------------------------------------------------------------------------
// Helper: generate unique ID
// ---------------------------------------------------------------------------

let elementIdCounter = 0;
function generateElementId(): string {
  return `element-${Date.now()}-${++elementIdCounter}`;
}

// ---------------------------------------------------------------------------
// Default layout
// ---------------------------------------------------------------------------

function createDefaultLayout(): MapLayout {
  return {
    mapFrame: {
      id: generateElementId(),
      type: 'map',
      x: 15,
      y: 30,
      width: 180,
      height: 220,
    },
    title: {
      id: generateElementId(),
      type: 'title',
      x: 15,
      y: 10,
      width: 180,
      height: 15,
      content: 'Map Title',
      style: { fontSize: 16 },
    },
    legend: {
      id: generateElementId(),
      type: 'legend',
      x: 155,
      y: 35,
      width: 40,
      height: 60,
    },
    scaleBar: {
      id: generateElementId(),
      type: 'scale-bar',
      x: 15,
      y: 255,
      width: 60,
      height: 10,
    },
    northArrow: {
      id: generateElementId(),
      type: 'north-arrow',
      x: 180,
      y: 255,
      width: 15,
      height: 15,
    },
    textBoxes: [],
  };
}

// ---------------------------------------------------------------------------
// Draggable Element Component
// ---------------------------------------------------------------------------

interface DraggableElementProps {
  element: LayoutElement;
  elementKey: string;
  selected: boolean;
  onSelect: (key: string) => void;
  onDragEnd: (key: string, x: number, y: number) => void;
  onResize: (key: string, width: number, height: number) => void;
  scale: number;
}

const DraggableElement: React.FC<DraggableElementProps> = ({
  element,
  elementKey,
  selected,
  onSelect,
  onDragEnd,
  onResize,
  scale,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, elemX: 0, elemY: 0 });
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(elementKey);

      if ((e.target as HTMLElement).classList.contains('resize-handle')) {
        setIsResizing(true);
        resizeStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          width: element.width,
          height: element.height,
        };
      } else {
        setIsDragging(true);
        dragStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          elemX: element.x,
          elemY: element.y,
        };
      }
    },
    [element, elementKey, onSelect]
  );

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = (e.clientX - dragStartRef.current.x) / scale;
        const dy = (e.clientY - dragStartRef.current.y) / scale;
        const newX = Math.max(0, dragStartRef.current.elemX + dx);
        const newY = Math.max(0, dragStartRef.current.elemY + dy);
        onDragEnd(elementKey, newX, newY);
      } else if (isResizing) {
        const dx = (e.clientX - resizeStartRef.current.x) / scale;
        const dy = (e.clientY - resizeStartRef.current.y) / scale;
        const newWidth = Math.max(10, resizeStartRef.current.width + dx);
        const newHeight = Math.max(10, resizeStartRef.current.height + dy);
        onResize(elementKey, newWidth, newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, elementKey, onDragEnd, onResize, scale]);

  const getElementLabel = (): string => {
    switch (element.type) {
      case 'map':
        return 'Map Frame';
      case 'title':
        return element.content ?? 'Title';
      case 'legend':
        return 'Legend';
      case 'scale-bar':
        return 'Scale Bar';
      case 'north-arrow':
        return '↑ N';
      case 'text':
        return element.content ?? 'Text';
      default:
        return element.type;
    }
  };

  const getElementColor = (): string => {
    switch (element.type) {
      case 'map':
        return '#e3f2fd';
      case 'title':
        return '#fff3e0';
      case 'legend':
        return '#e8f5e9';
      case 'scale-bar':
        return '#fce4ec';
      case 'north-arrow':
        return '#f3e5f5';
      case 'text':
        return '#fffde7';
      default:
        return '#f5f5f5';
    }
  };

  return (
    <Box
      ref={elementRef}
      onMouseDown={handleMouseDown}
      sx={{
        position: 'absolute',
        left: element.x * scale,
        top: element.y * scale,
        width: element.width * scale,
        height: element.height * scale,
        border: selected ? '2px solid #1976d2' : '1px solid #999',
        borderRadius: '2px',
        backgroundColor: getElementColor(),
        cursor: isDragging ? 'grabbing' : 'grab',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        userSelect: 'none',
        zIndex: selected ? 10 : 1,
        '&:hover': {
          borderColor: '#1976d2',
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          textAlign: 'center',
          fontSize: Math.max(8, 10 * scale),
          color: '#555',
          pointerEvents: 'none',
        }}
      >
        {getElementLabel()}
      </Typography>

      {/* Resize handle */}
      {selected && (
        <Box
          className="resize-handle"
          sx={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            width: 8,
            height: 8,
            backgroundColor: '#1976d2',
            cursor: 'nwse-resize',
            borderRadius: '50%',
          }}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Layout Composer Main Component
// ---------------------------------------------------------------------------

const LayoutComposer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { layoutComposerOpen, currentLayout, layoutTemplates } = useAppSelector(
    (state) => state.printExport
  );

  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadMenuAnchor, setLoadMenuAnchor] = useState<null | HTMLElement>(null);
  const [templateName, setTemplateName] = useState('');

  // Scale factor for rendering (mm to pixels on screen)
  const scale = 2.5;

  // Initialize layout when composer opens
  useEffect(() => {
    if (layoutComposerOpen && !currentLayout) {
      dispatch(setCurrentLayout(createDefaultLayout()));
    }
  }, [layoutComposerOpen, currentLayout, dispatch]);

  // Fetch templates when composer opens
  useEffect(() => {
    if (layoutComposerOpen) {
      dispatch(fetchLayoutTemplates());
    }
  }, [layoutComposerOpen, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(closeLayoutComposer());
    setSelectedElement(null);
  }, [dispatch]);

  const handleDragEnd = useCallback(
    (elementKey: string, x: number, y: number) => {
      dispatch(updateLayoutElement({ elementKey, updates: { x, y } }));
    },
    [dispatch]
  );

  const handleResize = useCallback(
    (elementKey: string, width: number, height: number) => {
      dispatch(updateLayoutElement({ elementKey, updates: { width, height } }));
    },
    [dispatch]
  );

  const handleAddElement = useCallback(
    (type: LayoutElementType) => {
      const newElement: LayoutElement = {
        id: generateElementId(),
        type,
        x: 50,
        y: 50,
        width: type === 'text' ? 60 : type === 'north-arrow' ? 15 : 40,
        height: type === 'text' ? 15 : type === 'north-arrow' ? 15 : 30,
        content: type === 'title' ? 'Title' : type === 'text' ? 'Text' : undefined,
        style: type === 'title' ? { fontSize: 16 } : { fontSize: 10 },
      };
      dispatch(addLayoutElement(newElement));
    },
    [dispatch]
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedElement && selectedElement !== 'mapFrame') {
      dispatch(removeLayoutElement(selectedElement));
      setSelectedElement(null);
    }
  }, [dispatch, selectedElement]);

  const handleSaveTemplate = useCallback(() => {
    if (currentLayout && templateName.trim()) {
      dispatch(saveLayoutTemplate({ name: templateName.trim(), layout: currentLayout }));
      setSaveDialogOpen(false);
      setTemplateName('');
    }
  }, [dispatch, currentLayout, templateName]);

  const handleLoadTemplate = useCallback(
    (template: { layout: MapLayout }) => {
      dispatch(setCurrentLayout(template.layout));
      setLoadMenuAnchor(null);
    },
    [dispatch]
  );

  const handleDeleteTemplate = useCallback(
    (templateId: string) => {
      dispatch(deleteLayoutTemplate(templateId));
    },
    [dispatch]
  );

  if (!currentLayout) return null;

  // Collect all elements for rendering
  const elements: Array<{ key: string; element: LayoutElement }> = [];
  elements.push({ key: 'mapFrame', element: currentLayout.mapFrame });
  if (currentLayout.title) elements.push({ key: 'title', element: currentLayout.title });
  if (currentLayout.legend) elements.push({ key: 'legend', element: currentLayout.legend });
  if (currentLayout.scaleBar) elements.push({ key: 'scaleBar', element: currentLayout.scaleBar });
  if (currentLayout.northArrow)
    elements.push({ key: 'northArrow', element: currentLayout.northArrow });
  currentLayout.textBoxes.forEach((tb, i) => {
    elements.push({ key: `textBox-${i}`, element: tb });
  });

  return (
    <Dialog
      open={layoutComposerOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="layout-composer-title"
    >
      <DialogTitle id="layout-composer-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Map Layout Composer</Typography>
          <IconButton onClick={handleClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Toolbar */}
        <Toolbar variant="dense" sx={{ borderBottom: 1, borderColor: 'divider', gap: 0.5 }}>
          <Tooltip title="Add Title">
            <IconButton size="small" onClick={() => handleAddElement('title')}>
              <TitleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Legend">
            <IconButton size="small" onClick={() => handleAddElement('legend')}>
              <LegendIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Scale Bar">
            <IconButton size="small" onClick={() => handleAddElement('scale-bar')}>
              <ScaleBarIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add North Arrow">
            <IconButton size="small" onClick={() => handleAddElement('north-arrow')}>
              <NorthArrowIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Text Box">
            <IconButton size="small" onClick={() => handleAddElement('text')}>
              <TextIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Map Frame">
            <IconButton size="small" onClick={() => handleAddElement('map')}>
              <AddIcon />
              <MapIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Tooltip title="Delete Selected">
            <span>
              <IconButton
                size="small"
                onClick={handleDeleteSelected}
                disabled={!selectedElement || selectedElement === 'mapFrame'}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Tooltip title="Save Template">
            <IconButton size="small" onClick={() => setSaveDialogOpen(true)}>
              <SaveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Load Template">
            <IconButton size="small" onClick={(e) => setLoadMenuAnchor(e.currentTarget)}>
              <LoadIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {/* Canvas area */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            bgcolor: '#e0e0e0',
            minHeight: 600,
            overflow: 'auto',
          }}
          onClick={() => setSelectedElement(null)}
        >
          {/* Page representation */}
          <Paper
            elevation={3}
            sx={{
              position: 'relative',
              width: 210 * scale,
              height: 297 * scale,
              bgcolor: 'white',
            }}
          >
            {elements.map(({ key, element }) => (
              <DraggableElement
                key={key}
                element={element}
                elementKey={key}
                selected={selectedElement === key}
                onSelect={setSelectedElement}
                onDragEnd={handleDragEnd}
                onResize={handleResize}
                scale={scale}
              />
            ))}
          </Paper>
        </Box>

        {/* Save template dialog */}
        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
          <DialogTitle>Save Layout Template</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Template Name"
              fullWidth
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveTemplate}
              disabled={!templateName.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Load template menu */}
        <Menu
          anchorEl={loadMenuAnchor}
          open={Boolean(loadMenuAnchor)}
          onClose={() => setLoadMenuAnchor(null)}
        >
          {layoutTemplates.length === 0 ? (
            <MenuItem disabled>No saved templates</MenuItem>
          ) : (
            layoutTemplates.map((template) => (
              <MenuItem key={template.id}>
                <ListItemText
                  primary={template.name}
                  onClick={() => handleLoadTemplate(template)}
                />
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTemplate(template.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </MenuItem>
            ))
          )}
        </Menu>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="outlined" startIcon={<ImageIcon />}>
          Export Image
        </Button>
        <Button variant="contained" startIcon={<PdfIcon />}>
          Export PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LayoutComposer;
